import React, { useState, useEffect, useRef } from 'react';
import { useBrands } from '../../../../hooks/useBrands';
import styles from './BrandAttributeField.module.scss';

const BrandAttributeField = ({
    attribute,
    value,
    error,
    onAttributeChange,
    clearAttributeError
}) => {
    const { name, label } = attribute;
    const [brandSearchTerm, setBrandSearchTerm] = useState(value || '');
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const searchTimeoutRef = useRef(null);

    const { brandSuggestions, loading, searchBrands, clearSearch, hasFetched } = useBrands();

    useEffect(() => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (brandSearchTerm.trim()) {
            setIsSearching(true);
            // Add debouncing to reduce API calls
            searchTimeoutRef.current = setTimeout(() => {
                searchBrands(brandSearchTerm);
            }, 300); // 300ms delay
        } else {
            setIsSearching(false);
            clearSearch();
        }

        // Cleanup timeout on unmount or when brandSearchTerm changes
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [brandSearchTerm]);

    const handleBrandSelect = (brand) => {
        const brandName = typeof brand === 'string' ? brand : brand.name;
        const brandId = typeof brand === 'string' ? null : (brand.brand_id || null);

        setBrandSearchTerm(brandName || '');
        setSelectedBrand(brand);

        // Update both brand name and brand_id in parent component
        // We'll pass an object containing both values
        onAttributeChange(name, {
            name: brandName,
            brand_id: brandId
        });

        setShowBrandDropdown(false);
        clearAttributeError(name);
        setIsSearching(false);

        // Clear any pending search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        setBrandSearchTerm(inputValue);

        // When typing, only update the brand name, clear brand_id
        onAttributeChange(name, {
            name: inputValue,
            brand_id: null
        });

        // If user clears input or types something different, clear selected brand
        if (inputValue !== selectedBrand?.name) {
            setSelectedBrand(null);
        }

        // Show dropdown when user starts typing
        if (inputValue.trim() && hasFetched) {
            setShowBrandDropdown(true);
        }

        clearAttributeError(name);
    };

    const handleInputFocus = () => {
        clearAttributeError(name);
        if (hasFetched && !showBrandDropdown) {
            setShowBrandDropdown(true);
        }
    };

    const handleInputBlur = () => {
        // Delay hiding dropdown to allow for clicks
        setTimeout(() => {
            setShowBrandDropdown(false);
            setIsSearching(false);
        }, 200);
    };

    const handleCustomBrand = () => {
        // When user wants to use custom brand name
        const customBrand = {
            name: brandSearchTerm,
            brand_id: null,
            global_identifier: brandSearchTerm
        };
        handleBrandSelect(customBrand);
    };

    const showTopBrands = !isSearching && brandSearchTerm === '';

    return (
        <div className={`${styles.label_input} ${error ? styles.error : ''}`}>
            <label>
                {label} Name
                {attribute.is_mandatory === 1 && <span>*</span>}
            </label>
            <div className={styles.input_container}>
                <div className={styles.search_container}>
                    <input
                        type="text"
                        name={name}
                        value={brandSearchTerm}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        autoComplete="off"
                        placeholder="Type to search brands or enter custom brand"
                        // placeholder={`Search or enter ${label.toLowerCase()}`}
                        className={styles.input_field}
                    />

                    {loading && (
                        <div className={styles.loading_indicator}>
                            Loading brands...
                        </div>
                    )}

                    {showBrandDropdown && hasFetched && (
                        <div className={styles.brand_dropdown}>
                            {/* Show search status */}
                            {/* {isSearching && brandSearchTerm && (
                                <div className={styles.search_status}>
                                    Searching for "{brandSearchTerm}"
                                </div>
                            )} */}

                            {/* Show top brands when no search */}
                            {/* {showTopBrands && (
                                <div className={styles.section_header}>
                                    Popular Brands
                                </div>
                            )} */}

                            {/* Always show "No Brand" option */}
                            {/* <div
                                className={styles.brand_option}
                                onMouseDown={() => handleBrandSelect('No Brand')}
                            >
                                <div className={styles.brand_name}>No Brand</div>
                            </div> */}

                            {/* Show brand suggestions */}
                            {brandSuggestions.length > 0 ? (
                                brandSuggestions.map((brand) => (
                                    <div
                                        key={brand.brand_id}
                                        className={styles.brand_option}
                                        onMouseDown={() => handleBrandSelect(brand)}
                                    >
                                        <div className={styles.brand_name}>{brand.name}</div>
                                    </div>
                                ))
                            ) : null}

                            {/* isSearching && brandSearchTerm ? (
                                <div className={styles.no_results}>
                                    No brands found. You can use "{brandSearchTerm}" as brand name.
                                </div>
                            ) : */}

                            {/* Show "No Brand" option when there are no brand suggestions */}
                            {brandSuggestions.length === 0 && brandSearchTerm.trim() !== '' && (
                                <div
                                    className={styles.brand_option}
                                    onMouseDown={() => handleBrandSelect({
                                        name: 'No Brand',
                                        brand_id: null,
                                        global_identifier: 'No Brand'
                                    })}
                                >
                                    <div className={styles.brand_name}>No Brand</div>
                                </div>
                            )}

                            {/* Show custom brand option if user typed something not in suggestions */}
                            {brandSearchTerm &&
                                brandSearchTerm !== 'No Brand' &&
                                !brandSuggestions.some(brand =>
                                    brand.name.toLowerCase() === brandSearchTerm.toLowerCase()
                                ) && (
                                    <div
                                        className={`${styles.brand_option} ${styles.custom_brand}`}
                                        onMouseDown={handleCustomBrand}
                                    >
                                        <div className={styles.brand_name}>Use "{brandSearchTerm}" as brand name</div>
                                    </div>
                                )}

                            {/* Show hint for large dataset */}
                            {/* {showTopBrands && brandSuggestions.length > 0 && (
                                <div className={styles.dataset_info}>
                                    Type to search more brands...
                                </div>
                            )} */}
                        </div>
                    )}

                    {!hasFetched && !loading && (
                        <div className={styles.loading_indicator}>
                            Loading brand database...
                        </div>
                    )}
                </div>
                {error && <span className={styles.errorMessage}>{error}</span>}
            </div>
        </div>
    );
};

export default BrandAttributeField;